import {VanComponent} from "@utils/vanComponent.ts";
import van, {type State} from "vanjs-core";
import {VanUtil, VanUtil as vu} from "@utils/vanUtil.ts";
import {LoginState} from "@login/loginState.ts";
import {Busy} from "@commons/layout/busy.ts";
import {Net} from "@utils/net.ts";
import type {VerifyResetPasswordCodeResponse} from "pnyx-dto";
import type {SendResetPasswordCodeResponse} from "pnyx-dto";
import type {MaskitoMask} from "@maskito/core/src/lib/types/mask";
import {Maskito} from "@maskito/core";
import {maskitoWithPlaceholder} from '@maskito/kit';
const {div, a, progress, h1, p, form, label, input} = van.tags;

export interface IForgotPasswordPromptCodeParent
{
    onReturnToSignIn(): void;
    onChangePassword(codeResponse: VerifyResetPasswordCodeResponse): void;
}

export type ForgotPasswordPromptCodeOptions =
{
    data: SendResetPasswordCodeResponse;
    parent: IForgotPasswordPromptCodeParent;
}

type InternalForm =
{
    userName: string;
    code: State<string>;
}

export class ForgotPasswordPromptCode extends VanComponent
{
    #parent: IForgotPasswordPromptCodeParent;
    #data: SendResetPasswordCodeResponse;
    #form: InternalForm;
    private readonly progress:HTMLElement;

    constructor(args: ForgotPasswordPromptCodeOptions)
    {
        super();

        this.#parent = args.parent;
        this.#data = args.data;

        this.#form = {
            userName: args.data.userName,
            code: van.state(this.generatePlaceholder()),
        };

        this.progress = Busy.visibility(progress({ "aria-label": "Submitting form" }));
    }
    
    generateMask(): MaskitoMask
    {
        let mask: MaskitoMask = [];
        for (let i = 0; i < this.#data.resetPasswordCodeLength; i++)
            mask.push(/\d/);
        return mask;
    }
    
    generatePlaceholder(): string
    {
        let placeholder: string = '';
        for (let i = 0; i < this.#data.resetPasswordCodeLength; i++)
            placeholder += '_';
        return placeholder;
    }

    toDom(): HTMLElement
    {
        const { plugins, preprocessors, postprocessors } = maskitoWithPlaceholder(this.generatePlaceholder());
        
        let inputHtml = input(VanUtil.turnPasswordManagers({ class: 'login-input form-control letter-spacing', id: 'prompt-code', tabIndex: 1,
                type: 'text', required: 'required', autofocus: true,
                spellcheck: false, autocapitalize: 'none',
                autocomplete: "one-time-code",
                value: () => this.#form.code.val ?? '',
                oninput: (e: Event) => this.onInput(e),
            }));
        
        let mask: MaskitoMask = this.generateMask();
        // @ts-ignore
        const maskedInput = new Maskito(inputHtml, 
            { 
                mask: mask, 
                overwriteMode: 'replace',
                preprocessors,
                postprocessors,
                plugins,
            },
        );

        let message;
        if (this.#data.sentViaEmail)
            message = 'Check your email for the one-time security code.';
        else
            message = 'Check your phone for the one-time security code.';
        
        return div(this.toDomTags({"class": "login-form-pane"}),
            p({ class: 'login-kicker' }, 'Account Recovery'),
            h1({ class: 'login-title' }, 'Verify Code'),
            p({ class: 'login-intro' }, message ),
            form({ class: 'login-form', onsubmit: event => this.submit(event) },
                div({class: "login-field"},
                    label({class: "login-label", for: 'prompt-code'}, 'One-Time Security Code'),
                    div({ class: 'login-input-wrap' }, inputHtml),
                    vu.load(vu.focalOnLoad)
                ),
                div(
                    div({class: "mt-1 mb-1"}, this.progress),
                ),
                div({ class: "text-center" },
                    a({ class: 'link-plain text-body-secondary', onclick: () => this.#parent.onReturnToSignIn() }, 'Return to sign-in')
                )
            )
        );
    }
    
    onInput(event: Event)
    {
        LoginState.instance.alert.hide();
        let inputElement: HTMLInputElement = event.target as HTMLInputElement;
        let code: string = inputElement.value;
        this.#form.code.val = code;
        
        if (code.length === this.#data.resetPasswordCodeLength && !code.includes("_"))
        {
            let form: HTMLFormElement|null = inputElement.closest('form');
            if (form)
                form.requestSubmit();
        }
    }

    async submit(event: Event): Promise<void>
    {
        LoginState.instance.alert.hide();
        event.preventDefault();

        let response: VerifyResetPasswordCodeResponse = await Net.post<VerifyResetPasswordCodeResponse>({
            url: '/forgotPassword/verifyCode', data: this.#form
        });
        if (!response.isValid)
        {
            LoginState.instance.alert.show("Invalid code");
            this.#form.code.val = '';       //NOTE: does not set to initial placeholder because the cursor would remoain on the right
            VanUtil.focusPanel(event.target);
            return;
        }
        
        this.#parent.onChangePassword(response);
    }
}